import { useEffect, useMemo, useState } from "react";
import { api, hoyISO, toISODateLocal } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Search, ArrowLeft, History, ChevronDown, ChevronUp, ArrowUpDown } from "lucide-react";

const cop = (n) => `$ ${Math.round(n || 0).toLocaleString("es-CO")}`;
const num = (n) => Number(n || 0).toLocaleString("es-CO", { maximumFractionDigits: 2 });
const hace = (d) => { const x = new Date(); x.setDate(x.getDate() - d); return toISODateLocal(x); };
const ESTADO = { despachado: "bg-emerald-100 text-emerald-800", entregado: "bg-emerald-100 text-emerald-800", confirmado: "bg-amber-100 text-amber-800", borrador: "bg-muted text-muted-foreground", cancelado: "bg-rose-100 text-rose-800" };

/** Productos → Historial de ventas: qué se vendió de cada producto, a quién, quién lo montó y de qué lote salió. */
export default function HistorialVentasPage() {
  const _sp = new URLSearchParams(window.location.search); // ?desde=&hasta= (desde el panel principal)
  const [desde, setDesde] = useState(_sp.get("desde") || hace(90));
  const [hasta, setHasta] = useState(_sp.get("hasta") || hoyISO());
  const [incluirCancelados, setIncluirCancelados] = useState(false);
  const [q, setQ] = useState("");
  const [resumen, setResumen] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ key: "ventas", dir: -1 });

  const params = () => `desde=${desde}&hasta=${hasta}&incluir_cancelados=${incluirCancelados}`;
  const cargarResumen = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/inventario/historial-ventas?${params()}`); setResumen(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cargar el historial"); }
    finally { setLoading(false); }
  };
  const abrir = async (pid) => {
    setLoading(true);
    try { const { data } = await api.get(`/inventario/historial-ventas?producto_id=${pid}&${params()}`); setDetalle(data); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cargar el producto"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargarResumen(); if (detalle) abrir(detalle.producto.id); }, [desde, hasta, incluirCancelados]);

  const filas = useMemo(() => {
    if (!resumen) return [];
    const t = q.trim().toLowerCase();
    const arr = resumen.filas.filter((f) => !t || [f.producto, f.codigo].some((s) => (s || "").toLowerCase().includes(t)));
    return arr.sort((a, b) => (typeof a[sort.key] === "string" ? String(a[sort.key] || "").localeCompare(String(b[sort.key] || ""), "es") : ((a[sort.key] ?? 0) - (b[sort.key] ?? 0))) * sort.dir);
  }, [resumen, q, sort]);
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : (typeof (resumen?.filas?.[0]?.[key]) === "string" ? 1 : -1) }));

  return (
    <div className="space-y-6" data-testid="historial-ventas-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Historial de ventas</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-1">{detalle ? (detalle.producto?.nombre || "Producto eliminado") : "¿Qué se ha vendido y a quién?"}</h1>
          <p className="text-sm text-muted-foreground mt-1">{detalle ? `${detalle.producto?.codigo || detalle.producto?.sku || "—"} · stock actual ${num(detalle.producto?.stock_actual)} · borradores no cuentan como venta` : "Elige un producto para ver cada venta: cliente, vendedor, quién montó el pedido, estado y el lote del que salió."}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2" data-testid="historial-filtros">
          <div><div className="text-[10px] text-muted-foreground mb-1">Desde</div><Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-10" data-testid="historial-desde" /></div>
          <div><div className="text-[10px] text-muted-foreground mb-1">Hasta</div><Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-10" data-testid="historial-hasta" /></div>
          <label className="flex items-center gap-2 text-sm h-10 px-3 rounded-md border border-input cursor-pointer" data-testid="historial-cancelados">
            <input type="checkbox" checked={incluirCancelados} onChange={(e) => setIncluirCancelados(e.target.checked)} className="accent-primary" /> Incluir cancelados
          </label>
          {detalle && <Button variant="outline" onClick={() => setDetalle(null)} className="h-10" data-testid="historial-volver"><ArrowLeft className="w-4 h-4 mr-2" /> Todos los productos</Button>}
        </div>
      </header>

      {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="historial-loading"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>}

      {detalle ? <Detalle d={detalle} /> : resumen && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Kpi label="Productos vendidos" value={num(resumen.totales.productos)} testid="historial-kpi-productos" />
            <Kpi label="Unidades" value={num(resumen.totales.unidades)} testid="historial-kpi-unidades" />
            <Kpi label="Ventas" value={cop(resumen.totales.ventas)} accent testid="historial-kpi-ventas" />
          </div>
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto o código…" className="h-10 pl-9" data-testid="historial-buscar" />
          </div>
          <div className="rounded-xl border border-border overflow-auto max-h-[65vh]" data-testid="historial-tabla-resumen">
            <table className="w-full text-xs">
              <thead className="bg-muted/60 sticky top-0 z-10"><tr>
                <Th label="Producto" k="producto" sort={sort} onSort={toggleSort} right={false} /><Th label="Unidades" k="unidades" sort={sort} onSort={toggleSort} /><Th label="Ventas" k="ventas" sort={sort} onSort={toggleSort} /><Th label="Pedidos" k="pedidos" sort={sort} onSort={toggleSort} /><Th label="Clientes" k="clientes" sort={sort} onSort={toggleSort} /><Th label="Última venta" k="ultima_venta" sort={sort} onSort={toggleSort} />
              </tr></thead>
              <tbody>
                {filas.map((f, i) => (
                  <tr key={f.producto_id} className="border-t border-border/60 tabular-nums cursor-pointer hover:bg-muted/30" onClick={() => abrir(f.producto_id)} data-testid={`historial-prod-${i}`}>
                    <td className="p-2 max-w-[320px]"><div className="truncate font-medium">{f.producto}</div><div className="text-muted-foreground">{f.codigo}</div></td>
                    <td className="p-2 text-right">{num(f.unidades)}</td><td className="p-2 text-right font-semibold">{cop(f.ventas)}</td><td className="p-2 text-right">{f.pedidos}</td><td className="p-2 text-right">{f.clientes}</td><td className="p-2 text-right">{f.ultima_venta}</td>
                  </tr>
                ))}
                {filas.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Sin ventas en este periodo.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Detalle({ d }) {
  const [abierto, setAbierto] = useState(null);
  const t = d.totales;
  return (
    <div className="space-y-4" data-testid="historial-detalle">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi label="Ventas" value={cop(t.ventas)} sub={`${t.ventas_count} línea(s) de pedido`} accent testid="historial-det-ventas" />
        <Kpi label="Unidades" value={num(t.unidades)} sub={`precio prom. ${cop(t.precio_promedio)}`} testid="historial-det-unidades" />
        <Kpi label="Costo (FIFO)" value={cop(t.costo)} sub="de los lotes consumidos" />
        <Kpi label="Utilidad" value={cop(t.utilidad)} sub={t.margen_pct != null ? `${t.margen_pct}% margen` : ""} />
        <Kpi label="Clientes" value={num(t.clientes)} />
        <Kpi label="Precio ficha" value={`${cop(d.producto?.precio_minimo)} / ${cop(d.producto?.precio_sugerido)}`} sub="mín / sugerido" />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Ranking titulo="Por cliente" items={d.por_cliente} testid="historial-por-cliente" />
        <Ranking titulo="Por vendedor" items={d.por_vendedor} testid="historial-por-vendedor" />
      </div>
      <div className="rounded-xl border border-border overflow-auto max-h-[60vh]" data-testid="historial-tabla-detalle">
        <table className="w-full text-xs">
          <thead className="bg-muted/60 sticky top-0 z-10"><tr>
            <th className="text-left p-2">Fecha</th><th className="text-left p-2">Pedido</th><th className="text-left p-2">Cliente</th><th className="text-left p-2">Vendedor</th><th className="text-left p-2">Montado por</th><th className="text-left p-2">Estado</th>
            <th className="text-right p-2">Cant.</th><th className="text-right p-2">Precio</th><th className="text-right p-2">Subtotal</th><th className="text-right p-2">Costo</th><th className="text-left p-2">Lote / contenedor</th>
          </tr></thead>
          <tbody>
            {d.filas.map((f, i) => (
              <FilaVenta key={`${f.pedido_id}-${i}`} f={f} i={i} open={abierto === i} onToggle={() => setAbierto(abierto === i ? null : i)} />
            ))}
            {d.filas.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-muted-foreground">Este producto no tiene ventas en el periodo.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilaVenta({ f, i, open, onToggle }) {
  const lotesTxt = f.lotes.length ? [...new Set(f.lotes.map((l) => l.contenedor))].join(", ") : (f.estado === "despachado" || f.estado === "entregado" ? "Sin lote" : "Pendiente de despacho");
  return (
    <>
      <tr className="border-t border-border/60 tabular-nums cursor-pointer hover:bg-muted/30" onClick={onToggle} data-testid={`historial-venta-${i}`}>
        <td className="p-2 whitespace-nowrap">{f.fecha}</td>
        <td className="p-2 font-medium whitespace-nowrap">{f.numero}{f.es_surtido && <span className="ml-1 text-[10px] text-violet-600">surtido</span>}</td>
        <td className="p-2 truncate max-w-[180px]">{f.cliente || "—"}</td>
        <td className="p-2 truncate max-w-[140px]">{f.vendedor || "—"}</td>
        <td className="p-2 truncate max-w-[140px]">{f.montado_por || "—"}</td>
        <td className="p-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${ESTADO[f.estado] || "bg-muted"}`}>{f.estado}</span></td>
        <td className="p-2 text-right">{num(f.cantidad)}</td><td className="p-2 text-right">{cop(f.precio_unitario)}</td><td className="p-2 text-right font-semibold">{cop(f.subtotal)}</td>
        <td className="p-2 text-right text-muted-foreground">{cop(f.costo_total)}{f.costo_fuente === "ficha" && <span title="Sin despacho: costo de ficha">*</span>}</td>
        <td className="p-2 truncate max-w-[200px]"><span className="inline-flex items-center gap-1">{lotesTxt}{f.lotes.length > 0 && (open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}</span></td>
      </tr>
      {open && f.lotes.length > 0 && (
        <tr className="bg-muted/20" data-testid={`historial-venta-lotes-${i}`}>
          <td colSpan={11} className="p-2 text-[11px]">
            <div className="flex flex-wrap gap-4">
              {f.lotes.map((l, j) => <span key={j} className="tabular-nums">{l.contenedor}{l.recibido ? ` (recibido ${l.recibido})` : ""}: <b>{num(l.cantidad)}</b> und @ {cop(l.costo_unitario)}</span>)}
              {f.despachado_por && <span className="text-muted-foreground">Despachó: {f.despachado_por}{f.despachado_at ? ` · ${f.despachado_at}` : ""}</span>}
              {f.notas && <span className="text-muted-foreground">Nota: {f.notas}</span>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Ranking({ titulo, items, testid }) {
  const max = items[0]?.ventas || 1;
  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><History className="w-3 h-3" /> {titulo}</div>
      <div className="mt-2 space-y-1.5">
        {items.slice(0, 6).map((c) => (
          <div key={c.nombre} className="text-xs">
            <div className="flex justify-between gap-2"><span className="truncate">{c.nombre}</span><span className="tabular-nums text-muted-foreground whitespace-nowrap">{num(c.unidades)} und · {cop(c.ventas)}</span></div>
            <div className="h-1 rounded bg-muted mt-0.5"><div className="h-1 rounded bg-primary" style={{ width: `${Math.max(3, (c.ventas / max) * 100)}%` }} /></div>
          </div>
        ))}
        {items.length === 0 && <div className="text-xs text-muted-foreground">—</div>}
      </div>
    </div>
  );
}

function Th({ label, k, sort, onSort, right = true }) {
  const active = sort.key === k;
  return (
    <th className={`p-2 ${right ? "text-right" : "text-left"} select-none cursor-pointer whitespace-nowrap`} onClick={() => onSort(k)} data-testid={`historial-th-${k}`}>
      <span className={`inline-flex items-center gap-1 ${active ? "text-primary" : ""}`}>{label}{active ? (sort.dir < 0 ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}</span>
    </th>
  );
}

function Kpi({ label, value, sub, accent, testid }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-xl font-bold tabular-nums mt-0.5 truncate" title={value}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground truncate" title={sub}>{sub}</div>}
    </div>
  );
}
